// Dependencies
import React from 'react';
import uuid from 'react-uuid';
import PropTypes from 'prop-types';
import { Link } from 'gatsby';
import parse from 'html-react-parser';
import truncate from 'lodash.truncate';
import striptags from 'striptags';

// Components
import BlogPostFeaturedImage from '../blogPostFeaturedImage/blogPostFeaturedImage';
import Image from '../../image/image';

// Hooks
import useBrandingOptions from '../../../hooks/useBrandingOptions';

// Images
import CupidBow from '../../../img/icons/cupid-bow.svg';

// Styles
import BlogPostStyles from './blogPost.styles';

export default function BlogPost({ post, large, filtered }) {
  const { title, featuredImage, uri, modified, blogFlexibleContent } = post;
  const categories = post?.categories?.nodes;

  const author = post?.author?.node;
  const blogProfile = author?.blogProfile;
  const profilePhoto = blogProfile?.profilePhoto1 || blogProfile?.profilePhoto2;
  const firstName = author?.firstName;
  const lastName = author?.lastName;

  const brandingOptions = useBrandingOptions();

  const logoImg = brandingOptions?.logo?.localFile?.publicURL;
  const logoAlt = brandingOptions?.logo?.altText || 'Maclynn logo';

  const excerpt = blogFlexibleContent?.excerpt;
  const excerptMaxCharCount = 200;
  const strippedExcerpt = striptags(excerpt);
  const truncatedExcerpt = truncate(strippedExcerpt, {
    length: excerptMaxCharCount,
    separator: ' ',
    omission: '...',
  });

  const readingTime = blogFlexibleContent?.readingTime;

  return (
    <BlogPostStyles key={uuid()} large={large}>
      <div className="inner">
        <Link to={uri} title={title} className="top">
          {!filtered &&
            (featuredImage?.node?.gatsbyImage ? (
              <BlogPostFeaturedImage featuredImage={featuredImage} />
            ) : (
              <div className="featured-img">
                {logoImg && (
                  <img
                    className="logo"
                    src={logoImg}
                    alt={logoAlt}
                    width={350}
                    height={90}
                  />
                )}
              </div>
            ))}

          {filtered && (
            <div
              className={`featured-img ${featuredImage?.src ? 'filter' : ''}`}
            >
              {featuredImage?.src && (
                <img src={featuredImage?.src} alt={featuredImage?.alt} />
              )}

              {!featuredImage?.src && logoImg && (
                <img
                  className="logo"
                  src={logoImg}
                  alt={logoAlt}
                  width={350}
                  height={90}
                />
              )}
            </div>
          )}
        </Link>

        <div className="bottom">
          <ul className="categories">
            {!!categories?.length &&
              categories.map((category) => (
                <li key={uuid()} className="category">
                  <Link to={uri}>{category?.name}</Link>
                </li>
              ))}
          </ul>

          <div className="meta">
            <Link
              to={author?.uri}
              title={`${firstName}'s Posts`}
              className="top"
            >
              {!filtered &&
                (profilePhoto ? (
                  <Image
                    image={profilePhoto}
                    className="profile-photo"
                    filter={false}
                  />
                ) : (
                  <div className="profile-photo has-icon">
                    <img
                      src={CupidBow}
                      width={22}
                      alt="Cupid bow and arrow"
                      className="icon"
                    />
                  </div>
                ))}

              {filtered && (
                <div className="profile-photo">
                  <img src={profilePhoto?.src} alt={profilePhoto?.alt} />
                </div>
              )}
            </Link>
            <div className="bottom">
              {firstName && (
                <Link to={author?.uri} className="name">
                  {firstName} {lastName}
                </Link>
              )}
              <Link to={uri} className="article-info" itemScope>
                <span
                  className="date"
                  itemProp="dateModified"
                  content={modified}
                >
                  {modified}
                </span>
                <span className="pipe"> | </span>
                <span className="reading-time">{readingTime}</span>
              </Link>
            </div>
          </div>

          <Link to={uri} className="title-wrap">
            <h3 className="title">{parse(title || '')}</h3>
            <p className="excerpt">{parse(truncatedExcerpt || '')}</p>
            <div className="separator" />
            <div className="btn text-only">Read More</div>
          </Link>
        </div>
      </div>
    </BlogPostStyles>
  );
}

BlogPost.propTypes = {
  post: PropTypes.shape({
    title: PropTypes.string,
    excerpt: PropTypes.string,
    uri: PropTypes.string,
    date: PropTypes.string,
    modified: PropTypes.string,
    blogFlexibleContent: PropTypes.shape({
      excerpt: PropTypes.string,
      readingTime: PropTypes.shape(),
    }),
    featuredImage: PropTypes.shape({
      src: PropTypes.string,
      alt: PropTypes.string,
      node: PropTypes.shape({
        gatsbyImage: PropTypes.shape({}),
      }),
    }),
    categories: PropTypes.shape({
      nodes: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string,
        }),
      ),
    }),
    author: PropTypes.shape({
      node: PropTypes.shape({}),
    }),
  }).isRequired,
  large: PropTypes.bool,
  filtered: PropTypes.bool,
};

BlogPost.defaultProps = {
  large: false,
  filtered: false,
};
